import React, { memo, useMemo, useState, useRef } from "react";
import {
	Box,
	Typography,
	Paper,
	Grid,
	CircularProgress,
	Button,
	Alert,
	Skeleton,
	Chip,
	Tooltip,
	IconButton,
	Popover,
	Checkbox,
	FormControlLabel,
	FormGroup,
	Divider,
} from "@mui/material";
import { Launch, KeyboardArrowDown } from "@mui/icons-material";
import SearchableMultiSelect from "../misc/SearchableMultiSelect";
import { updateVehicleBand } from "../../services/api";
import useUserAccess from "../../hooks/useUserAccess";
import { Bar } from "react-chartjs-2";
import {
	Chart as ChartJS,
	CategoryScale,
	LinearScale,
	BarElement,
	Title,
	Tooltip as ChartTooltip,
	Legend,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";

// Register ChartJS components
ChartJS.register(
	CategoryScale,
	LinearScale,
	BarElement,
	Title,
	ChartTooltip,
	Legend,
	ChartDataLabels,
);

// Allowed band values for the vehicle band dropdown (must match backend allow-list)
const BAND_OPTIONS = ["M-Band", "K-Band", "C-Band"];

// Removed old generic VFF_COLORS array; using explicit color mapping for status_color
const VFF_COLOR_MAP = {
	"light green": { bg: "#91b900", border: "#91b900" }, // light green for Available for Build
	yellow: { bg: "#ffff00", border: "#cccc00" }, // Delivery date missing
	orange: { bg: "#ffcd00", border: "#e6a300" }, // Nomination missing
	available: { bg: "#91b900", border: "#7ca000" }, // green for available
	red: { bg: "#e40045", border: "#b30000" },
	blue: { bg: "#4b96d2", border: "#4b96d2" },
	green: { bg: "#91b900", border: "#7ca000" },
	gray: { bg: "#6b7280", border: "#5b6370" },
	grey: { bg: "#6b7280", border: "#5b6370" },
	purple: { bg: "#800080", border: "#660066" },
	// Legacy support for German names
	tra: { bg: "#858585", border: "#0d0d0d" },
	transparent: { bg: "#858585", border: "#0d0d0d" },
	blau: { bg: "#4b96d2", border: "#4b96d2" },
	gelb: { bg: "#ffcd00", border: "#e6a300" },
	grau: { bg: "#6b7280", border: "#5b6370" },
	grün: { bg: "#91b900", border: "#7ca000" },
	grun: { bg: "#91b900", border: "#7ca000" },
	rot: { bg: "#e40045", border: "#b30000" },
	lila: { bg: "#800080", border: "#660066" },
};

// Status color to descriptive label mapping
const VFF_STATUS_LABELS = {
	// English
	"light green": "Available for Build",
	blue: "Alternatives",
	yellow: "Delivery Date missing",
	orange: "Nomination missing",
	red: "Delayed",
	// German equivalents
	blau: "Alternatives",
	gelb: "Nomination missing",
	rot: "Delayed",
	// Additional colors that might appear
	gray: "Unknown Status",
	grey: "Unknown Status",
	purple: "Special Status",
	lila: "Special Status",
	tra: "Transparent",
	transparent: "Transparent",
};

// Define the desired order for status colors in legend
const STATUS_ORDER = [
	// Available for Build (1st - should be shown first)
	"light green",
	// Alternatives (2nd)
	"blue",
	"blau",
	// Nomination missing (3rd)
	"yellow",
	"gelb",
	// Delivery Date missing (4th)
	"orange",
	// Delayed (5th)
	"red",
	"rot",
];

// Function to sort status colors according to desired order
function sortStatusColors(statusColors) {
	return Array.from(statusColors).sort((a, b) => {
		const aKey = a.toString().trim().toLowerCase();
		const bKey = b.toString().trim().toLowerCase();

		const aIndex = STATUS_ORDER.indexOf(aKey);
		const bIndex = STATUS_ORDER.indexOf(bKey);

		// If both are in the predefined order, sort by their position
		if (aIndex !== -1 && bIndex !== -1) {
			return aIndex - bIndex;
		}

		// If only one is in predefined order, prioritize it
		if (aIndex !== -1) return -1;
		if (bIndex !== -1) return 1;

		// If neither is in predefined order, sort alphabetically
		return aKey.localeCompare(bKey);
	});
}

function getVffStatusLabel(statusColor) {
	if (!statusColor) return "Unknown Status";
	const key = statusColor.toString().trim().toLowerCase();
	return VFF_STATUS_LABELS[key] || statusColor; // Fallback to original name if no mapping found
}

function getVffColor(type) {
	if (!type) return { bg: "#303c49", border: "#1a2129" };
	const key = type.toString().trim().toLowerCase();
	const color = VFF_COLOR_MAP[key] || { bg: "#303c49", border: "#1a2129" };

	// Debug logging for red segments
	if (key === "rot") {
		console.log("🔴 Red segment detected:", { type, key, color });
	}

	return color;
}

// Shared chart options
const baseChartOptions = {
	responsive: true,
	maintainAspectRatio: false,
	layout: { padding: { top: 10, bottom: 10, left: 10, right: 10 } },
	plugins: {
		legend: {
			position: "top",
			align: "start",
			labels: {
				font: { family: "MAN EUROPE_Bold", size: 12 },
				usePointStyle: true,
				padding: 20,
				boxWidth: 12,
			},
			onHover: (e) => {
				e.native.target.style.cursor = "pointer";
			},
			onLeave: (e) => {
				e.native.target.style.cursor = "default";
			},
		},
		datalabels: { display: false },
	},
};

const VffDashboard = memo(
	({
		vffChartData,
		error,
		loading,
		anflugTypes,
		vehicleNumbers,
		programms,
		vffPartNumbers,
		vffPurchaseTypes,
		selectedVffProgramm,
		selectedVehicles,
		selectedVffPartNo,
		selectedVffPurchaseType,
		tempSelectedVffProgramm,
		tempSelectedVehicles,
		tempSelectedVffPartNo,
		tempSelectedVffPurchaseType,
		handleVffProgrammChange,
		handleVehicleChange,
		handleVffPartNoChange,
		handleVffPurchaseTypeChange,
		handleApplyVffFilters,
		handleResetVffFilters,
		fetchVffData,
		totalVehicles,
		totalAnflugTypes,
		totalParts,
		isChartLoading,
		hasVffFiltersChanged,
	}) => {
		// View mode state - 'vehicle' for default vehicle view, 'timeline' for Gantt view, 'timeline-latest' for earliest date per vehicle
		const [viewMode, setViewMode] = useState("vehicle");
		const [barModalOpen, setBarModalOpen] = useState(false);
		const [barModalData, setBarModalData] = useState(null); // { item, index, totalCount, availableCount }

		// Band editing state — each vehicle has its own dropdown popover with radio
		// options and Save/Cancel inside it.
		// - savedBands: values persisted this session (reflected without a refetch)
		// - bandMenuAnchor / bandMenuVehicle: which row's popover is open
		// - bandDraft: the radio selection inside the open popover (not yet saved)
		// - savingVehicle: vehicle_no currently being saved (drives the spinner)
		const [savedBands, setSavedBands] = useState({});
		const [bandMenuAnchor, setBandMenuAnchor] = useState(null);
		const [bandMenuVehicle, setBandMenuVehicle] = useState(null);
		const [bandDraft, setBandDraft] = useState([]); // array of selected band options
		const [savingVehicle, setSavingVehicle] = useState(null);
		const [bandError, setBandError] = useState(null);

		// Gate the band dropdown behind write access (backend re-checks regardless).
		const userEmail = localStorage.getItem("username") || "";
		const { canWrite } = useUserAccess(userEmail);

		// The currently effective band value for a vehicle (saved override or original).
		// Stored as a comma-separated string; parse into an array for the checkboxes.
		const getBandValue = (item) => {
			if (savedBands[item.vehicle_no] !== undefined) {
				return savedBands[item.vehicle_no];
			}
			return item.band || "";
		};

		const parseBands = (value) =>
			(value || "")
				.split(",")
				.map((s) => s.trim())
				.filter(Boolean);

		const openBandMenu = (event, item) => {
			event.stopPropagation();
			setBandMenuAnchor(event.currentTarget);
			setBandMenuVehicle(item.vehicle_no);
			setBandDraft(parseBands(getBandValue(item)));
			setBandError(null);
		};

		const closeBandMenu = () => {
			if (savingVehicle) return; // don't close mid-save
			setBandMenuAnchor(null);
			setBandMenuVehicle(null);
			setBandDraft([]);
			setBandError(null);
		};

		const toggleBandOption = (option) => {
			setBandDraft((prev) =>
				prev.includes(option)
					? prev.filter((o) => o !== option)
					: [...prev, option],
			);
		};

		const saveBandMenu = async () => {
			const vehicleNo = bandMenuVehicle;
			if (!vehicleNo) return;
			// Preserve the canonical option order when joining the selection
			const joined = BAND_OPTIONS.filter((o) => bandDraft.includes(o)).join(
				", ",
			);
			setSavingVehicle(vehicleNo);
			setBandError(null);
			try {
				await updateVehicleBand(vehicleNo, joined);
				setSavedBands((prev) => ({ ...prev, [vehicleNo]: joined }));
				setSavingVehicle(null);
				setBandMenuAnchor(null);
				setBandMenuVehicle(null);
				setBandDraft([]);
			} catch (err) {
				console.error("❌ Failed to save band for", vehicleNo, err);
				setBandError(
					err?.response?.data?.detail || err?.message || "Failed to save band",
				);
				setSavingVehicle(null);
			}
		};

		// Process chart data based on view mode - MOVED UP to be defined first
		const processedChartData = useMemo(() => {
			if (!vffChartData || !vffChartData.data) return null;

			const data = vffChartData.data;
			if (viewMode === "vehicle") {
				// Vehicle view: Data is already aggregated by vehicle in backend
				// Each item represents a unique vehicle with aggregated data
				const uniqueVehicles = [
					...new Set(data.map((item) => item.vehicle_no)),
				]; // Sort vehicles by build_date (earliest first)
				const vehicleLabels = uniqueVehicles.sort((a, b) => {
					// Find the earliest build_date for each vehicle
					const getVehicleBuildDate = (vehicleNo) => {
						const vehicleEntries = data.filter(
							(item) => item.vehicle_no === vehicleNo,
						);
						let earliestBuildDate = null;
						vehicleEntries.forEach((entry) => {
							if (entry.build_date) {
								const entryDate = new Date(entry.build_date);
								if (
									!earliestBuildDate ||
									entryDate < new Date(earliestBuildDate)
								) {
									earliestBuildDate = entry.build_date;
								}
							}
						});
						return earliestBuildDate;
					};

					const dateA = getVehicleBuildDate(a);
					const dateB = getVehicleBuildDate(b);

					// Handle null/undefined dates - put them at the end
					if (!dateA && !dateB) return a.localeCompare(b); // Sort by vehicle number if both have no dates
					if (!dateA) return 1; // Vehicle A goes to end
					if (!dateB) return -1; // Vehicle B goes to end

					// Sort by date (earliest first)
					return new Date(dateA) - new Date(dateB);
				});

				// Get all unique status colors for datasets, excluding available/green since it's shown separately
				const allStatusColors = new Set();
				data.forEach((item) => {
					if (item.status_stacks) {
						Object.keys(item.status_stacks).forEach((status) => {
							// Exclude available/green status colors as they're shown in the Available column
							// But include 'light green' as it represents "Available for Build" status
							const statusLower = status.toString().toLowerCase();
							if (
								statusLower !== "available" &&
								statusLower !== "green" &&
								statusLower !== "grün" &&
								statusLower !== "grun"
							) {
								allStatusColors.add(status);
							}
						});
					}
				});

				const statusColorArray = sortStatusColors(allStatusColors);

				// Create datasets for each status color
				const datasets = statusColorArray.map((statusColor) => {
					const statusData = vehicleLabels.map((vehicle) => {
						// For each vehicle, sum up the counts for this status color from all data entries
						const vehicleEntries = data.filter(
							(item) => item.vehicle_no === vehicle,
						);
						return vehicleEntries.reduce((sum, item) => {
							return sum + (item.status_stacks?.[statusColor] || 0);
						}, 0);
					});

					const color = getVffColor(statusColor);

					return {
						label: statusColor || "Unknown",
						data: statusData,
						backgroundColor: color.bg,
						borderColor: color.border,
						borderWidth: 1,
						maxBarThickness: 40,
					};
				});

				// Get totals and available for each vehicle (sum from all entries for same vehicle)
				const totals = vehicleLabels.map((vehicle) => {
					const vehicleEntries = data.filter(
						(item) => item.vehicle_no === vehicle,
					);
					return vehicleEntries.reduce(
						(sum, item) => sum + (item.total_count || 0),
						0,
					);
				});

				const available = vehicleLabels.map((vehicle) => {
					const vehicleEntries = data.filter(
						(item) => item.vehicle_no === vehicle,
					);
					return vehicleEntries.reduce(
						(sum, item) => sum + (item.available_count || 0),
						0,
					);
				}); // Filter raw data to show only unique vehicles for the table
				const uniqueRawData = vehicleLabels.map((vehicle) => {
					const vehicleEntries = data.filter(
						(item) => item.vehicle_no === vehicle,
					); // Take the first entry but with aggregated totals
					const firstEntry = vehicleEntries[0];
					// Find the earliest build_date from all entries for this vehicle
					let earliestBuildDate = null;
					vehicleEntries.forEach((entry) => {
						if (entry.build_date) {
							const entryDate = new Date(entry.build_date);
							if (
								!earliestBuildDate ||
								entryDate < new Date(earliestBuildDate)
							) {
								earliestBuildDate = entry.build_date;
							}
						}
					});

					// Debug log to verify we're getting the earliest date
					if (vehicleEntries.length > 1) {
						console.log(
							`Vehicle ${vehicle}: Found ${vehicleEntries.length} entries`,
							{
								allEntries: vehicleEntries.map((e, i) => ({
									index: i,
									build_date: e.build_date,
									date: e.date,
									bedarfstermin: e.bedarfstermin,
								})),
								allDates: vehicleEntries
									.map((e) => e.build_date)
									.filter(Boolean),
								earliestDate: earliestBuildDate,
								firstEntryDate: firstEntry.build_date,
							},
						);
					}

					return {
						...firstEntry,
						total_count: totals[vehicleLabels.indexOf(vehicle)],
						available_count: available[vehicleLabels.indexOf(vehicle)],
						build_date: earliestBuildDate, // Always use earliest, don't fallback to first
						status_stacks: statusColorArray.reduce((stacks, statusColor) => {
							stacks[statusColor] = vehicleEntries.reduce((sum, item) => {
								return sum + (item.status_stacks?.[statusColor] || 0);
							}, 0);
							return stacks;
						}, {}),
					};
				});
				return {
					labels: vehicleLabels,
					datasets,
					totals,
					available,
					rawData: uniqueRawData,
				};
			} else if (viewMode === "timeline-latest") {
				// Timeline by latest: Show only the earliest date for each vehicle
				const vehicleLatestMap = new Map();

				// Find the earliest date for each vehicle
				data.forEach((item) => {
					const vehicleNo = item.vehicle_no;
					const currentDate = new Date(item.date);

					if (
						!vehicleLatestMap.has(vehicleNo) ||
						currentDate < new Date(vehicleLatestMap.get(vehicleNo).date)
					) {
						vehicleLatestMap.set(vehicleNo, item);
					}
				});

				// Convert map to array and sort by vehicle number
				const latestData = Array.from(vehicleLatestMap.values()).sort((a, b) =>
					a.vehicle_no.localeCompare(b.vehicle_no),
				);

				const labels = latestData.map(
					(item) => `${item.vehicle_no}\n${item.date}`,
				);

				// Get all unique status colors for datasets, excluding available/green since it's shown separately
				const allStatusColors = new Set();
				latestData.forEach((item) => {
					if (item.status_stacks) {
						Object.keys(item.status_stacks).forEach((status) => {
							// Exclude available/green status colors as they're shown in the Available column
							const statusLower = status.toString().toLowerCase();
							if (
								statusLower !== "available" &&
								statusLower !== "green" &&
								statusLower !== "grün" &&
								statusLower !== "grun"
							) {
								allStatusColors.add(status);
							}
						});
					}
				});

				const statusColorArray = sortStatusColors(allStatusColors);

				// Create datasets for each status color
				const datasets = statusColorArray.map((statusColor) => {
					const statusData = latestData.map((item) => {
						return item.status_stacks?.[statusColor] || 0;
					});

					const color = getVffColor(statusColor);

					return {
						label: statusColor || "Unknown",
						data: statusData,
						backgroundColor: color.bg,
						borderColor: color.border,
						borderWidth: 1,
						maxBarThickness: 40,
					};
				});

				return {
					labels,
					datasets,
					totals: latestData.map((item) => item.total_count || 0),
					available: latestData.map((item) => item.available_count || 0),
					rawData: latestData,
				};
			} else {
				// Timeline view: Show each vehicle-date combination as separate row
				const labels = data.map((item) => `${item.vehicle_no}\n${item.date}`);

				// Get all unique status colors for datasets, excluding available/green since it's shown separately
				const allStatusColors = new Set();
				data.forEach((item) => {
					if (item.status_stacks) {
						Object.keys(item.status_stacks).forEach((status) => {
							// Exclude available/green status colors as they're shown in the Available column
							const statusLower = status.toString().toLowerCase();
							if (
								statusLower !== "available" &&
								statusLower !== "green" &&
								statusLower !== "grün" &&
								statusLower !== "grun"
							) {
								allStatusColors.add(status);
							}
						});
					}
				});

				const statusColorArray = sortStatusColors(allStatusColors);

				// Create datasets for each status color
				const datasets = statusColorArray.map((statusColor) => {
					const statusData = data.map((item) => {
						return item.status_stacks?.[statusColor] || 0;
					});

					const color = getVffColor(statusColor);

					return {
						label: statusColor || "Unknown",
						data: statusData,
						backgroundColor: color.bg,
						borderColor: color.border,
						borderWidth: 1,
						maxBarThickness: 40,
					};
				});

				return {
					labels,
					datasets,
					totals: data.map((item) => item.total_count || 0),
					available: data.map((item) => item.available_count || 0),
					rawData: data,
				};
			}
		}, [vffChartData, viewMode]);

		// Chart height calculations - NOW using processedChartData that's already defined
		const barHeight = 35; // Made slimmer - reduced from 40 to 35
		const maxVisibleBars = 15;
		const containerHeight = maxVisibleBars * barHeight;
		const totalHeight = processedChartData
			? Math.max(processedChartData.labels.length * barHeight, 400)
			: 400;

		// Dynamic padding and width based on number of bars
		const barCount = processedChartData ? processedChartData.labels.length : 0;
		const isFilteredData = barCount <= 20; // Extended threshold to handle up to 20 bars
		const dynamicRightPadding = isFilteredData ? "150px" : "20px";
		// Remove fixed minWidth to prevent horizontal overflow
		const dynamicMinWidth = "100%";
		// Helper function to get active filters information for display
		const getActiveFiltersInfo = () => {
			const activeFilters = [];

			if (
				selectedVffProgramm &&
				selectedVffProgramm.length > 0 &&
				!selectedVffProgramm.includes("all")
			) {
				activeFilters.push({
					label: "Projects",
					count: selectedVffProgramm.length,
					values: selectedVffProgramm,
				});
			}

			if (
				selectedVehicles &&
				selectedVehicles.length > 0 &&
				!selectedVehicles.includes("all")
			) {
				activeFilters.push({
					label: "Vehicles",
					count: selectedVehicles.length,
					values: selectedVehicles,
				});
			}

			if (
				selectedVffPartNo &&
				selectedVffPartNo.length > 0 &&
				!selectedVffPartNo.includes("all")
			) {
				activeFilters.push({
					label: "Part Numbers",
					count: selectedVffPartNo.length,
					values: selectedVffPartNo,
				});
			}

			if (
				selectedVffPurchaseType &&
				selectedVffPurchaseType.length > 0 &&
				!selectedVffPurchaseType.includes("all")
			) {
				activeFilters.push({
					label: "Purchase Types",
					count: selectedVffPurchaseType.length,
					values: selectedVffPurchaseType,
				});
			}

			return activeFilters;
		};

		const activeFiltersInfo = getActiveFiltersInfo();
		const totalActiveFilters = activeFiltersInfo.reduce(
			(sum, filter) => sum + filter.count,
			0,
		);
		// Calculate grand totals for each chart label (vehicle or vehicle-date combination)
		const chartGrandTotals = useMemo(() => {
			if (!processedChartData) return {};

			const totals = {};
			processedChartData.labels.forEach((label, index) => {
				const total = processedChartData.datasets.reduce((sum, dataset) => {
					return sum + (dataset.data[index] || 0);
				}, 0);
				totals[label] = total;
			});
			return totals;
		}, [processedChartData]);
		// Chart click handler to redirect to VFF page with filters
		const handleChartClick = (event, elements) => {
			if (!elements || elements.length === 0) return;

			const element = elements[0];
			const datasetIndex = element.datasetIndex;
			const index = element.index;

			if (
				!processedChartData ||
				!processedChartData.datasets ||
				!processedChartData.labels
			)
				return;

			const dataset = processedChartData.datasets[datasetIndex];
			const statusColor = dataset.label; // This is the status color (status_color field)
			const count = dataset.data[index]; // Number of parts in this segment

			if (count === 0) return; // Don't redirect if no parts
			let vehicleNumber, date;
			if (viewMode === "timeline" || viewMode === "timeline-latest") {
				// Timeline view: extract vehicle and date from label
				const fullLabel = processedChartData.labels[index]; // "Vehicle123\n2024-01-15"
				[vehicleNumber, date] = fullLabel.split("\n");
			} else {
				// Vehicle view: label is just the vehicle number
				vehicleNumber = processedChartData.labels[index];
				date = null; // No specific date in vehicle view
			} // Build the URL with filter parameters for the VFF page
			const baseUrl = `${window.location.origin}/vff`;
			const urlParams = new URLSearchParams({
				vehicle_filter: vehicleNumber,
				status_color_filter: statusColor, // Using status_color as the filter value
				from_dashboard: "true",
			});

			// Include Part Number Prefix filter if explicitly applied by user (not 'all')
			if (
				selectedVffPartNo &&
				selectedVffPartNo.length > 0 &&
				!selectedVffPartNo.includes("all")
			) {
				urlParams.set("part_no_prefix", selectedVffPartNo.join(","));
			}

			// Include Purchase Type filter if applied (not 'all')
			// Don't apply default purchase types to show all parts regardless of type
			if (
				selectedVffPurchaseType &&
				selectedVffPurchaseType.length > 0 &&
				!selectedVffPurchaseType.includes("all")
			) {
				urlParams.set(
					"purchase_type_filter",
					selectedVffPurchaseType.join(","),
				);
			}

			// Only include date filter for timeline views, not vehicle view
			if ((viewMode === "timeline" || viewMode === "timeline-latest") && date) {
				urlParams.set("date_filter", date);
			}
			const vffUrl = `${baseUrl}?${urlParams.toString()}`;

			// Open in new tab
			window.open(vffUrl, "_blank");
		};
		// Handle Available column redirect
		const handleAvailableRedirect = (item) => {
			// Get vehicle number and date info
			let vehicleNumber, date;
			if (viewMode === "timeline" || viewMode === "timeline-latest") {
				vehicleNumber = item.vehicle_no;
				date = item.date;
			} else {
				vehicleNumber = item.vehicle_no;
				date = null;
			} // Build the URL with filter parameters for the VFF page
			const baseUrl = `${window.location.origin}/vff`;
			const urlParams = new URLSearchParams({
				vehicle_filter: vehicleNumber,
				status_color_filter: "available", // Filter for available status
				from_dashboard: "true",
			});

			// Include Part Number Prefix filter if explicitly applied by user (not 'all')
			if (
				selectedVffPartNo &&
				selectedVffPartNo.length > 0 &&
				!selectedVffPartNo.includes("all")
			) {
				urlParams.set("part_no_prefix", selectedVffPartNo.join(","));
			}

			// Include Purchase Type filter if applied (not 'all')
			// Don't apply default purchase types to show all available parts regardless of type
			if (
				selectedVffPurchaseType &&
				selectedVffPurchaseType.length > 0 &&
				!selectedVffPurchaseType.includes("all")
			) {
				urlParams.set(
					"purchase_type_filter",
					selectedVffPurchaseType.join(","),
				);
			}

			// Only include date filter for timeline views, not vehicle view
			if ((viewMode === "timeline" || viewMode === "timeline-latest") && date) {
				urlParams.set("date_filter", date);
			}

			const vffUrl = `${baseUrl}?${urlParams.toString()}`;

			// Open in new tab
			window.open(vffUrl, "_blank");
		};

		// Enhanced chart options for VFF dashboard
		const vffChartOptions = {
			...baseChartOptions,
			indexAxis: "y",
			onClick: handleChartClick, // Add click handler
			layout: {
				padding: {
					left: 40, // Increased left padding for fixed y-axis title
					right: 100, // Increased right padding for grand total labels
					top: 0,
					bottom: 0,
				},
			},
			plugins: {
				...baseChartOptions.plugins,
				legend: { display: false },
				title: { display: false },
				datalabels: {
					display: true,
					color: (context) => {
						const backgroundColor = context.dataset.backgroundColor;
						if (typeof backgroundColor === "string") {
							// Use white text for dark colors and dark text for light colors
							if (
								backgroundColor.includes("rgba(30, 40, 50") ||
								backgroundColor.includes("rgba(204, 0, 0") ||
								backgroundColor.includes("rgba(107, 151, 0") ||
								backgroundColor.includes("rgba(84, 84, 84")
							) {
								return "white";
							} else {
								return "white";
							}
						}
						return "#ffffff";
					},
					anchor: "center",
					align: "center",
					offset: 0,
					font: {
						family: "MAN EUROPE_Regular",
						size: 12,
					},
					formatter: (value, context) => {
						// Only show label if value is greater than 0 and significant enough
						const total = chartGrandTotals[context.label] || 0;
						const percentage = total > 0 ? (value / total) * 100 : 0;

						// Show value only if it's more than 5% of the total or greater than 5
						if (value > 0 && (percentage > 5 || value > 5)) {
							return value.toLocaleString();
						}
						return "";
					},
					textStrokeColor: (context) => {
						const backgroundColor = context.dataset.backgroundColor;
						if (typeof backgroundColor === "string") {
							return backgroundColor.includes("#1e2832") ||
								backgroundColor.includes("#cc0000") ||
								backgroundColor.includes("#6b9700") ||
								backgroundColor.includes("#161616")
								? "rgba(0,0,0,0.5)"
								: "rgba(255,255,255,0.5)";
						}
						return "rgba(0,0,0,0.5)";
					},
					textStrokeWidth: 1,
				},
				tooltip: {
					backgroundColor: "rgba(255, 255, 255, 0.98)",
					titleColor: "#1e2832",
					bodyColor: "#333",
					borderColor: "#cc0000",
					borderWidth: 2,
					cornerRadius: 8,
					displayColors: true,
					titleFont: {
						family: "MAN EUROPE_Bold",
						size: 14,
					},
					bodyFont: {
						family: "MAN EUROPE_Regular",
						size: 12,
					},
					padding: 12,
					mode: "point",
					intersect: true,
					callbacks: {
						title: (context) => {
							if (context.length > 0) {
								const dataIndex = context[0].dataIndex;
								const fullLabel = context[0].label;
								// Get total and available counts from processedChartData
								const total = processedChartData?.totals?.[dataIndex] || 0;
								const available =
									processedChartData?.available?.[dataIndex] || 0;

								if (viewMode === "timeline" || viewMode === "timeline-latest") {
									const [vehicleNo, date] = fullLabel.split("\n");
									return [
										`Vehicle: ${vehicleNo}`,
										`Date: ${date}`,
										`Total: ${total.toLocaleString()} parts`,
										`Available: ${available.toLocaleString()} parts`,
									];
								} else {
									// Vehicle view - show aggregated data
									return [
										`Vehicle: ${fullLabel}`,
										`Total: ${total.toLocaleString()} parts`,
										`Available: ${available.toLocaleString()} parts`,
									];
								}
							}
							return "";
						},
						label: (context) => {
							const statusColor = context.dataset.label;
							const count = context.raw;
							const dataIndex = context.dataIndex;
							const total = processedChartData?.totals?.[dataIndex] || 0;
							const percentage =
								total > 0 ? ((count / total) * 100).toFixed(1) : "0.0";

							return `${statusColor}: ${count.toLocaleString()} parts (${percentage}%)`;
						},
						afterBody: (context) => {
							if (context.length > 0) {
								const dataIndex = context[0].dataIndex;
								const total = processedChartData?.totals?.[dataIndex] || 0;
								const available =
									processedChartData?.available?.[dataIndex] || 0;
								const unavailable = total - available;

								return [
									`\nBreakdown:`,
									`• Available: ${available.toLocaleString()} parts`,
									`• Unavailable: ${unavailable.toLocaleString()} parts`,
									`• Total: ${total.toLocaleString()} parts`,
								];
							}
							return [];
						},
					},
				},
			},
			scales: {
				x: {
					stacked: true,
					display: false,
					type: "linear",
					position: "bottom",
					beginAtZero: true,
					grid: {
						color: "rgba(0, 0, 0, 0.1)",
						drawOnChartArea: true,
						drawTicks: false,
					},
					max: (() => {
						if (!processedChartData || !processedChartData.datasets) return 100;

						// Calculate the maximum stacked value for each bar
						const maxStackedValue = Math.max(
							...processedChartData.labels.map((_, index) => {
								return processedChartData.datasets.reduce((sum, dataset) => {
									return sum + (dataset.data[index] || 0);
								}, 0);
							}),
						);

						const barCount = processedChartData.labels
							? processedChartData.labels.length
							: 0;
						// Apply more conservative padding to ensure all stack segments are visible
						let paddingMultiplier;
						if (barCount <= 5) {
							paddingMultiplier = 1.3; // Reduced padding for 1-5 bars
						} else if (barCount <= 10) {
							paddingMultiplier = 1.25; // Reduced padding for 6-10 bars
						} else if (barCount <= 20) {
							paddingMultiplier = 1.2; // Reduced padding for 11-20 bars
						} else {
							paddingMultiplier = 1.15; // Minimal padding for many bars (21+)
						}

						return Math.ceil(maxStackedValue * paddingMultiplier);
					})(),
				},
				y: {
					stacked: true,
					title: {
						display: false, // Disable chart's built-in title, we'll add our own fixed one
					},
					position: "left",
					grid: {
						display: true,
						color: "rgba(0, 0, 0, 0.05)",
						drawOnChartArea: true,
						drawTicks: false,
					},
					ticks: {
						font: { family: "MAN EUROPE_Regular", size: 10 },
						maxRotation: 0,
						minRotation: 0,
						autoSkip: false,
						padding: 8,
						maxTicksLimit: 20, // Limit number of ticks to prevent overcrowding
						callback: function (value, index, values) {
							// Handle multiline labels for timeline views (vehicle number and date)
							const label = this.getLabelForValue(value);
							if (
								(viewMode === "timeline" || viewMode === "timeline-latest") &&
								label &&
								typeof label === "string" &&
								label.includes("\n")
							) {
								const [vehicleNo, date] = label.split("\n");
								// Return as array for multiline display
								return [vehicleNo, date];
							}
							// For vehicle view, just return the vehicle number
							return label;
						},
					},
				},
			},
			barPercentage: 0.6, // Made slimmer - reduced from 0.8 to 0.6
			categoryPercentage: 0.8, // Reduced from 0.9 to 0.8 for more spacing
			maintainAspectRatio: false,
			responsive: true,
			animation: {
				onComplete: (chart) => {
					// Draw grand total labels at the right of each bar after animation completes
					if (
						!chart ||
						!chart.ctx ||
						!chart.data ||
						!chart.data.datasets ||
						!chart.data.labels
					) {
						return;
					}

					const ctx = chart.ctx;
					const datasets = chart.data.datasets;

					ctx.save();
					ctx.font = "11px MAN EUROPE_Regular";
					ctx.textAlign = "left";
					ctx.fillStyle = "#1e2832";
					chart.data.labels.forEach((label, index) => {
						const grandTotal = chartGrandTotals[label] || 0;
						if (grandTotal > 0) {
							// Calculate the right edge of the stacked bar by finding the furthest right point
							let maxRightX = 0;
							let cumulativeValue = 0;

							datasets.forEach((dataset, datasetIndex) => {
								const datasetMeta = chart.getDatasetMeta(datasetIndex);
								if (
									datasetMeta &&
									datasetMeta.data &&
									datasetMeta.data[index]
								) {
									const value = dataset.data[index] || 0;
									if (value > 0) {
										cumulativeValue += value;
										const xScale = chart.scales.x;
										if (xScale) {
											const currentX = xScale.getPixelForValue(cumulativeValue);
											maxRightX = Math.max(maxRightX, currentX);
										}
									}
								}
							});

							// Get the y position for the bar
							const firstDatasetMeta = chart.getDatasetMeta(0);
							if (
								firstDatasetMeta &&
								firstDatasetMeta.data &&
								firstDatasetMeta.data[index]
							) {
								const y = firstDatasetMeta.data[index].y;
								const x = maxRightX + 15; // Position to the right of the complete stacked bar

								// Draw grand total with enhanced styling
								const text = `Σ = ${grandTotal.toLocaleString()}`;
								const textWidth = ctx.measureText(text).width;

								// Background rectangle with border
								ctx.fillStyle = "rgba(255, 255, 255, 0.95)";
								ctx.fillRect(x - 2, y - 10, textWidth + 8, 20);

								// Border with MAN red color
								ctx.strokeStyle = "#cc0000";
								ctx.lineWidth = 1;
								ctx.strokeRect(x - 2, y - 10, textWidth + 8, 20);

								// Text in dark color
								ctx.fillStyle = "#1e2832";
								ctx.font = "10px MAN EUROPE_Regular";
								ctx.fillText(text, x + 2, y + 2);
							}
						}
					});

					ctx.restore();
				},
			},
		};

		// Debug logging for filter data
		console.log("🔍 VffDashboard received props:", {
			vffPartNumbers,
			vffPurchaseTypes,
			tempSelectedVffPartNo,
			tempSelectedVffPurchaseType,
			handleVffPartNoChange: !!handleVffPartNoChange,
			handleVffPurchaseTypeChange: !!handleVffPurchaseTypeChange,
		});

		return (
			<Box
				sx={{
					width: "100%",
					maxWidth: "100%",
					overflow: "hidden",
					backgroundColor: "#f0f0f0",
					minHeight: "100vh",
					boxSizing: "border-box", // Ensure padding is included in width calculations
				}}
			>
				{loading ? (
					<Box sx={{ p: 4 }}>
						<Skeleton
							variant="rectangular"
							width="100%"
							height={60}
							sx={{ mb: 2 }}
						/>
						<Skeleton variant="rectangular" width="100%" height={600} />
					</Box>
				) : error ? (
					<Alert severity="error" sx={{ mb: 2 }}>
						Error loading VFF data: {error}
						<Button onClick={fetchVffData} sx={{ ml: 2 }} color="error">
							Retry
						</Button>
					</Alert>
				) : processedChartData ? (
					<>
						{" "}
						<Grid container spacing={3} sx={{ mb: 3 }}>
							{[
								{
									title: "Total Vehicles",
									value: totalVehicles.toLocaleString(),
									description: "Total unique vehicle numbers",
									tourTarget: "vff-dash-total-vehicles",
								},
								{
									title: "Total Parts",
									value: totalParts.toLocaleString(),
									description: "Total parts across all vehicles",
									tourTarget: "vff-dash-total-parts",
								},
							].map((card) => (
								<Grid item xs={12} md={6} key={card.title}>
									<Paper
										data-tour-target={card.tourTarget}
										elevation={2}
										sx={{
											px: 2,
											py: 1,
											backgroundColor: "#fff",
											borderRadius: "8px",
											textAlign: "center",
										}}
									>
										<Typography
											variant="h6"
											sx={{ mb: 0.5, fontFamily: "MAN EUROPE_Bold" }}
										>
											{card.title}
										</Typography>
										<Typography
											variant="h4"
											sx={{ fontFamily: "MAN EUROPE_Bold", color: "#1e2832" }}
										>
											{card.value}
										</Typography>
										<Typography
											variant="body2"
											sx={{ mt: 0.5, color: "#545454" }}
										>
											{card.description}
										</Typography>
									</Paper>
								</Grid>
							))}
						</Grid>{" "}
						<Paper
							elevation={3}
							sx={{ p: 3, backgroundColor: "#fff", borderRadius: "8px", mb: 4 }}
						>
							<Box
								sx={{
									display: "flex",
									justifyContent: "space-between",
									alignItems: "flex-start",
									mb: 3,
								}}
							>
								<Box sx={{ flex: 1 }}>
									{" "}
									<Box
										sx={{
											display: "flex",
											justifyContent: "space-between",
											alignItems: "center",
											mb: 1,
										}}
									>
										<Typography
											variant="h5"
											sx={{ fontFamily: "MAN EUROPE_Bold", color: "#1e2832" }}
										>
											Part status per vehicle - Overview
										</Typography>

										{/* View Mode Toggle - moved inline and shifted left */}
										<Box
											data-tour-target="vff-dash-view"
											sx={{
												display: "flex",
												alignItems: "center",
												gap: 1,
												marginRight: "10px",
											}}
										>
											<Typography
												variant="body2"
												sx={{
													fontFamily: "MAN EUROPE_Bold",
													color: "#1e2832",
													fontSize: "0.8rem",
												}}
											>
												View:
											</Typography>
											<Button
												variant={
													viewMode === "vehicle" ? "contained" : "outlined"
												}
												onClick={() => setViewMode("vehicle")}
												size="small"
												sx={{
													fontFamily: "MAN EUROPE_Bold",
													fontSize: "0.7rem",
													backgroundColor:
														viewMode === "vehicle" ? "#cc0000" : "transparent",
													borderColor: "#cc0000",
													color: viewMode === "vehicle" ? "white" : "#cc0000",
													minWidth: "70px",
													height: "28px",
													padding: "4px 8px",
													"&:hover": {
														backgroundColor:
															viewMode === "vehicle"
																? "#aa0000"
																: "rgba(204, 0, 0, 0.04)",
														borderColor: "#cc0000",
													},
												}}
											>
												Vehicle
											</Button>{" "}
											<Button
												variant={
													viewMode === "timeline" ? "contained" : "outlined"
												}
												onClick={() => setViewMode("timeline")}
												size="small"
												sx={{
													fontFamily: "MAN EUROPE_Bold",
													fontSize: "0.7rem",
													backgroundColor:
														viewMode === "timeline" ? "#cc0000" : "transparent",
													borderColor: "#cc0000",
													color: viewMode === "timeline" ? "white" : "#cc0000",
													minWidth: "70px",
													height: "28px",
													padding: "4px 8px",
													"&:hover": {
														backgroundColor:
															viewMode === "timeline"
																? "#aa0000"
																: "rgba(204, 0, 0, 0.04)",
														borderColor: "#cc0000",
													},
												}}
											>
												Timeline
											</Button>
											<Button
												variant={
													viewMode === "timeline-latest"
														? "contained"
														: "outlined"
												}
												onClick={() => setViewMode("timeline-latest")}
												size="small"
												sx={{
													fontFamily: "MAN EUROPE_Bold",
													fontSize: "0.7rem",
													backgroundColor:
														viewMode === "timeline-latest"
															? "#cc0000"
															: "transparent",
													borderColor: "#cc0000",
													color:
														viewMode === "timeline-latest"
															? "white"
															: "#cc0000",
													minWidth: "90px",
													height: "28px",
													padding: "4px 8px",
													"&:hover": {
														backgroundColor:
															viewMode === "timeline-latest"
																? "#aa0000"
																: "rgba(204, 0, 0, 0.04)",
														borderColor: "#cc0000",
													},
												}}
											>
												Timeline by Earliest
											</Button>
										</Box>
									</Box>
									{/* Active Filters Display */}
									<Box
										sx={{
											display: "flex",
											alignItems: "center",
											gap: 1,
											mb: 1,
										}}
									>
										<Typography
											variant="body2"
											sx={{
												fontFamily: "MAN EUROPE_Regular",
												color: "#666",
												fontSize: "0.8rem",
											}}
										>
											Active Filters{" "}
											{totalActiveFilters > 0 && `(${totalActiveFilters})`}:
										</Typography>
										{activeFiltersInfo.length > 0 ? (
											<Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
												{activeFiltersInfo.map((filter, index) => (
													<Box
														key={index}
														sx={{
															display: "flex",
															flexDirection: "column",
															gap: 0.3,
														}}
													>
														<Typography
															variant="caption"
															sx={{
																fontSize: "0.65rem",
																fontWeight: "bold",
																color: "#cc0000",
																fontFamily: "MAN EUROPE_Regular",
															}}
														>
															{filter.label}:
														</Typography>
														<Box
															sx={{
																display: "flex",
																gap: 0.3,
																flexWrap: "wrap",
															}}
														>
															{filter.values.map((value, valueIndex) => (
																<Chip
																	key={valueIndex}
																	label={value}
																	size="small"
																	sx={{
																		backgroundColor: "#cc0000",
																		color: "white",
																		fontSize: "0.65rem",
																		height: "18px",
																		fontFamily: "MAN EUROPE_Regular",
																		"& .MuiChip-label": {
																			padding: "0 4px",
																		},
																	}}
																/>
															))}
														</Box>
													</Box>
												))}
											</Box>
										) : (
											<Typography
												variant="body2"
												sx={{
													color: "#999",
													fontSize: "0.75rem",
													fontFamily: "MAN EUROPE_Regular",
												}}
											>
												No active filters
											</Typography>
										)}
									</Box>
								</Box>
								<Box
									data-tour-target="vff-dash-filters"
									sx={{
										display: "flex",
										gap: 1.5,
										alignItems: "center",
										flexWrap: "wrap",
										justifyContent: "flex-end",
									}}
								>
									<Typography
										variant="body2"
										sx={{
											fontFamily: "MAN EUROPE_Bold",
											color: "#1e2832",
											fontSize: "0.8rem",
											minWidth: "50px",
											flexShrink: 0,
										}}
									>
										Filters:
									</Typography>

									{/* All Programms */}
									<SearchableMultiSelect
										label="All Programms"
										options={programms}
										value={(tempSelectedVffProgramm || []).filter(
											(v) => v !== "all",
										)}
										onChange={(arr) =>
											handleVffProgrammChange({
												target: { value: arr.length === 0 ? ["all"] : arr },
											})
										}
										minWidth={120}
									/>

									{/* All Vehicles */}
									<SearchableMultiSelect
										label="All Vehicles"
										options={vehicleNumbers}
										value={(tempSelectedVehicles || []).filter(
											(v) => v !== "all",
										)}
										onChange={(arr) =>
											handleVehicleChange({
												target: { value: arr.length === 0 ? ["all"] : arr },
											})
										}
										minWidth={110}
									/>

									{/* All Part Numbers */}
									<SearchableMultiSelect
										label="All Part Numbers"
										options={vffPartNumbers || []}
										value={(tempSelectedVffPartNo || []).filter(
											(v) => v !== "all",
										)}
										onChange={(arr) =>
											handleVffPartNoChange({
												target: { value: arr.length === 0 ? ["all"] : arr },
											})
										}
										minWidth={120}
									/>

									{/* All Purchase Types */}
									<SearchableMultiSelect
										label="All Purchase Types"
										options={vffPurchaseTypes || []}
										value={(tempSelectedVffPurchaseType || []).filter(
											(v) => v !== "all",
										)}
										onChange={(arr) =>
											handleVffPurchaseTypeChange({
												target: { value: arr.length === 0 ? ["all"] : arr },
											})
										}
										minWidth={130}
									/>

									<Button
										size="small"
										variant="outlined"
										onClick={handleResetVffFilters}
										sx={{
											fontFamily: "MAN EUROPE_Bold",
											fontSize: "0.7rem",
											borderColor: "#666",
											color: "#666",
											minWidth: "55px",
											height: "28px",
											padding: "4px 8px",
											flexShrink: 0,
											"&:hover": {
												borderColor: "#444",
												backgroundColor: "rgba(102, 102, 102, 0.04)",
											},
										}}
									>
										Reset
									</Button>
									<Button
										size="small"
										variant="contained"
										onClick={handleApplyVffFilters}
										disabled={!hasVffFiltersChanged}
										sx={{
											fontFamily: "MAN EUROPE_Bold",
											fontSize: "0.7rem",
											backgroundColor: hasVffFiltersChanged
												? "#cc0000"
												: "#ccc",
											color: "white",
											minWidth: "55px",
											height: "28px",
											padding: "4px 8px",
											flexShrink: 0,
											"&:hover": {
												backgroundColor: hasVffFiltersChanged
													? "#aa0000"
													: "#bbb",
											},
											"&:disabled": {
												backgroundColor: "#ccc",
												color: "#666",
											},
										}}
									>
										Apply
									</Button>
								</Box>{" "}
							</Box>{" "}
							{/* Custom Table-Style Chart */}
							<Box
								sx={{
									width: "100%",
									position: "relative", // Add position relative for loading overlay
								}}
							>
								{/* Loading Overlay */}
								{isChartLoading && (
									<Box
										display="flex"
										justifyContent="center"
										alignItems="center"
										sx={{
											position: "absolute",
											top: 0,
											left: 0,
											right: 0,
											bottom: 0,
											minHeight: "500px",
											backgroundColor: "rgba(255, 255, 255, 0.95)",
											zIndex: 1000,
											borderRadius: "8px",
											backdropFilter: "blur(2px)",
										}}
									>
										<Box sx={{ textAlign: "center" }}>
											<CircularProgress
												size={50}
												thickness={4}
												sx={{ color: "#cc0000", mb: 2 }}
											/>
											<Typography
												variant="body2"
												sx={{ color: "#666", fontFamily: "MAN EUROPE_Regular" }}
											>
												Loading filtered data...
											</Typography>
										</Box>
									</Box>
								)}
								{/* Legend */}
								<Box
									sx={{
										width: "100%",
										padding: "15px 0",
										backgroundColor: "#fff",
										borderBottom: "1px solid #eee",
										marginBottom: "10px",
									}}
								>
									<Box
										sx={{
											display: "flex",
											justifyContent: "space-between",
											alignItems: "center",
											padding: "0 20px",
										}}
									>
										{/* Legend */}
										<Box
											sx={{ display: "flex", flexWrap: "wrap", gap: "15px" }}
										>
											{processedChartData.datasets.map((dataset, index) => {
												const descriptiveLabel = getVffStatusLabel(
													dataset.label,
												);
												return (
													<Box
														key={index}
														sx={{
															display: "flex",
															alignItems: "center",
															marginRight: "15px",
														}}
													>
														<Box
															sx={{
																width: "12px",
																height: "12px",
																backgroundColor: dataset.backgroundColor,
																borderRadius: "2px",
																marginRight: "8px",
																flexShrink: 0,
															}}
														/>
														<Typography
															variant="body2"
															sx={{
																fontFamily: "MAN EUROPE_Regular",
																fontSize: "12px",
																whiteSpace: "nowrap",
															}}
														>
															{descriptiveLabel}
														</Typography>
													</Box>
												);
											})}
										</Box>
									</Box>
								</Box>{" "}
								{/* Table Header */}{" "}
								<Box
									sx={{
										display: "grid",
										gridTemplateColumns: "200px 100px 100px 1fr",
										gap: "0px",
										backgroundColor: "transparent",
										marginBottom: "2px",
										border: "1px solid #1e2832",
									}}
								>
									<Box
										sx={{
											backgroundColor: "#1e2832",
											color: "white",
											padding: "12px 16px",
											fontFamily: "MAN EUROPE_Bold",
											fontSize: "14px",
											textAlign: "center",
											borderRight: "1px solid #fff",
										}}
									>
										{viewMode === "vehicle"
											? "Vehicle No."
											: "Vehicle No. / Bedarfstermin"}
									</Box>
									<Box
										sx={{
											backgroundColor: "#1e2832",
											color: "white",
											padding: "12px 16px",
											fontFamily: "MAN EUROPE_Bold",
											fontSize: "14px",
											textAlign: "center",
											borderRight: "1px solid #fff",
										}}
									>
										Total
									</Box>
									<Box
										sx={{
											backgroundColor: "#1e2832",
											color: "white",
											padding: "12px 16px",
											fontFamily: "MAN EUROPE_Bold",
											fontSize: "14px",
											textAlign: "center",
											borderRight: "1px solid #fff",
										}}
									>
										In Stock
									</Box>{" "}
									<Box
										sx={{
											backgroundColor: "#1e2832",
											color: "white",
											padding: "12px 16px",
											fontFamily: "MAN EUROPE_Bold",
											fontSize: "14px",
											textAlign: "center",
											display: "flex",
											flexDirection: "column",
											alignItems: "center",
										}}
									>
										<span>Status Distribution</span>
										<span
											style={{
												fontSize: "10px",
												fontFamily: "MAN EUROPE_Regular",
												opacity: 0.8,
												marginTop: "2px",
											}}
										></span>
									</Box>
								</Box>
								{/* Table Rows */}
								<Box
									data-vff-scroll-container="true"
									sx={{
										maxHeight: "600px",
										overflowY: "auto",
										"&::-webkit-scrollbar": { width: "8px" },
										"&::-webkit-scrollbar-track": {
											background: "#f1f1f1",
											borderRadius: "4px",
										},
										"&::-webkit-scrollbar-thumb": {
											background: "#cc0000",
											borderRadius: "4px",
											"&:hover": { background: "#aa0000" },
										},
									}}
								>
									{processedChartData.rawData?.map((item, index) => {
										const totalCount = processedChartData.totals[index] || 0;
										const availableCount =
											processedChartData.available[index] || 0;
										const maxBarWidth = 800; // Maximum width for the bar chart - increased to use more horizontal space
										return (
											<Box
												key={index}
												sx={{
													display: "grid",
													gridTemplateColumns: "200px 100px 100px 1fr",
													gap: "0px",
													backgroundColor: "white",
													marginBottom: "2px",
													minHeight: "75px",
													border: "1px solid #e0e0e0",
													overflow: "hidden",
													"&:hover": {
														backgroundColor: "#f8f9f9",
													},
												}}
											>
												{/* Vehicle No. / Date Column */}
												<Box
													sx={{
														backgroundColor: "transparent",
														padding: "12px 16px",
														display: "flex",
														flexDirection: "column",
														justifyContent: "center",
														borderRight: "1px solid #e0e0e0",
													}}
												>
													{" "}
													<Typography
														sx={{
															fontFamily: "MAN EUROPE_Bold",
															fontSize: "13px",
															color: "#1e2832",
														}}
													>
														{item.vehicle_no}
													</Typography>
													{/* Show bedarfstermin date only for timeline views, not for vehicle view */}
													{(viewMode === "timeline" ||
														viewMode === "timeline-latest") &&
														item.date &&
														item.date !== "N/A" && (
															<Typography
																sx={{
																	fontFamily: "MAN EUROPE_Regular",
																	fontSize: "11px",
																	color: "#666",
																	marginTop: "2px",
																}}
															>
																{item.date}
															</Typography>
														)}{" "}
													{/* Show build_date only for vehicle view */}
													{viewMode === "vehicle" && item.build_date && (
														<Typography
															sx={{
																fontFamily: "MAN EUROPE_Regular",
																fontSize: "12px",
																color: "#666",
																marginTop: "2px",
															}}
														>
															Build: {(() => {
																// Convert yyyy-mm-dd or yyyy/mm/dd to dd/mm/yyyy format
																const buildDate = item.build_date;
																if (
																	buildDate &&
																	(buildDate.includes("-") ||
																		buildDate.includes("/"))
																) {
																	const parts = buildDate.includes("-")
																		? buildDate.split("-")
																		: buildDate.split("/");
																	if (parts.length === 3) {
																		return `${parts[2]}/${parts[1]}/${parts[0]}`;
																	}
																}
																return buildDate; // fallback to original if format doesn't match
															})()}
														</Typography>
													)}
													{/* Band dropdown trigger — opens a popover with radio options + Save/Cancel */}
													{(() => {
														const currentBand = getBandValue(item);
														const isRowSaving =
															savingVehicle === item.vehicle_no;
														return (
															<Button
																size="small"
																variant="outlined"
																disabled={!canWrite || isRowSaving}
																onClick={(e) => openBandMenu(e, item)}
																endIcon={
																	isRowSaving ? (
																		<CircularProgress
																			size={12}
																			sx={{ color: "#cc0000" }}
																		/>
																	) : (
																		<KeyboardArrowDown
																			sx={{ fontSize: "16px" }}
																		/>
																	)
																}
																sx={{
																	mt: 1,
																	minWidth: 110,
																	justifyContent: "space-between",
																	textTransform: "none",
																	fontFamily: "MAN EUROPE_Regular",
																	fontSize: "12px",
																	color: currentBand ? "#1e2832" : "#999",
																	borderColor: "#ccc",
																	backgroundColor: "#fff",
																	padding: "2px 8px",
																	"&:hover": { borderColor: "#cc0000" },
																}}
															>
																{currentBand || "Select band"}
															</Button>
														);
													})()}
												</Box>
												{/* Total Column */}
												<Box
													sx={{
														backgroundColor: "transparent",
														padding: "12px 16px",
														display: "flex",
														alignItems: "center",
														justifyContent: "center",
														borderRight: "1px solid #e0e0e0",
													}}
												>
													<Typography
														sx={{
															fontFamily: "MAN EUROPE_Bold",
															fontSize: "14px",
															color: "#1e2832",
														}}
													>
														{totalCount.toLocaleString()}
													</Typography>
												</Box>{" "}
												{/* Available Column */}
												<Box
													sx={{
														backgroundColor: "transparent",
														padding: "12px 16px",
														display: "flex",
														alignItems: "center",
														justifyContent: "center",
														borderRight: "1px solid #e0e0e0",
														gap: 1,
													}}
												>
													<Typography
														sx={{
															fontFamily: "MAN EUROPE_Bold",
															fontSize: "14px",
															color: "#137c13",
														}}
													>
														{availableCount.toLocaleString()}
													</Typography>
													<Tooltip title="Click to view available parts for this vehicle">
														<IconButton
															size="small"
															onClick={() => handleAvailableRedirect(item)}
															sx={{
																color: "#137c13",
																padding: "4px",
																"&:hover": {
																	backgroundColor: "rgba(145, 185, 0, 0.1)",
																	color: "#137c13",
																},
															}}
														>
															<Launch fontSize="small" />
														</IconButton>
													</Tooltip>{" "}
												</Box>
												{/* Status Distribution (Enhanced Stacked Bar) Column */}
												<Box
													sx={{
														backgroundColor: "transparent",
														padding: "12px 5px",
														display: "flex",
														alignItems: "center",
														minHeight: "70px",
														width: "100%",
														overflow: "hidden",
														minWidth: 0,
													}}
												>
													{" "}
													{/* Large Stacked Bar Container */}
													<Box
														className="stacked-bar-container"
														sx={{
															flex: 1,
															minWidth: 0,
															height: "50px",
															display: "flex",
															overflow: "hidden",
															borderRadius: "8px",
															boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
															border: "2px solid #e0e0e0",
															background:
																"linear-gradient(90deg, #f8f8f8 0%, #ffffff 100%)",
															position: "relative",
														}}
													>
														{processedChartData.datasets.map(
															(dataset, datasetIndex) => {
																const value = dataset.data[index] || 0;
																if (value === 0) return null;

																// Use fixed scale of 500 for consistent bar representation across all vehicles
																const fixedScale = 500;
																const percentage =
																	totalCount > 0
																		? (value / totalCount) * 100
																		: 0;
																const fixedScalePercentage =
																	(value / fixedScale) * 100;
																// Cap the width at 100% if value exceeds fixed scale
																const width = `${Math.min(fixedScalePercentage, 100)}%`;

																return (
																	<Tooltip
																		key={datasetIndex}
																		title={
																			<Box
																				sx={{
																					p: 2,
																					minWidth: "320px",
																					maxWidth: "400px",
																				}}
																			>
																				{" "}
																				<Box
																					sx={{
																						display: "flex",
																						alignItems: "center",
																						gap: "8px",
																						marginBottom: "12px",
																						borderBottom: "2px solid #cc0000",
																						paddingBottom: "6px",
																					}}
																				>
																					<img
																						src="/vehicle_icon.png"
																						alt="vehicle"
																						style={{
																							width: "25px",
																							height: "25px",
																							objectFit: "contain",
																						}}
																					/>
																					<Typography
																						sx={{
																							fontFamily: "MAN EUROPE_Bold",
																							fontSize: "15px",
																							color: "#1e2832",
																						}}
																					>
																						{viewMode === "vehicle" ? (
																							`Vehicle: ${item.vehicle_no}`
																						) : (
																							<>
																								{`Vehicle: ${item.vehicle_no} | `}
																								<img
																									src="/calendar.png"
																									alt="date"
																									style={{
																										width: "23px",
																										height: "23px",
																										objectFit: "contain",
																										marginRight: "3px",
																										verticalAlign: "middle",
																									}}
																								/>
																								{` Date: ${item.date}`}
																							</>
																						)}
																					</Typography>
																				</Box>
																				{/* Highlighted current segment */}
																				{/* <Box sx={{ 
                                      marginBottom: '12px', 
                                      padding: '10px',
                                      backgroundColor: 'rgba(204, 0, 0, 0.08)',
                                      borderRadius: '6px',
                                      border: '2px solid #cc0000'
                                    }}>
                                      <Typography sx={{
                                        fontFamily: 'MAN EUROPE_Bold',
                                        fontSize: '13px',
                                        color: '#cc0000',
                                        marginBottom: '4px'
                                      }}>
                                        📊 Selected Segment:
                                      </Typography>
                                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <Box sx={{
                                          width: '16px',
                                          height: '16px',
                                          backgroundColor: dataset.backgroundColor,
                                          marginRight: '8px',
                                          borderRadius: '3px',
                                          flexShrink: 0,
                                          border: '2px solid #cc0000'
                                        }} />
                                        <Typography sx={{
                                          fontFamily: 'MAN EUROPE_Bold',
                                          fontSize: '13px',
                                          color: '#1e2832',
                                          flex: 1
                                        }}>
                                          {getVffStatusLabel(dataset.label)}: {value.toLocaleString()} parts ({percentage.toFixed(1)}%)
                                        </Typography>
                                      </Box>
                                    </Box> */}
																				{/* Complete status breakdown */}
																				<Box sx={{ marginBottom: "12px" }}>
																					<Typography
																						sx={{
																							fontFamily: "MAN EUROPE_Bold",
																							fontSize: "12px",
																							color: "#1e2832",
																							marginBottom: "8px",
																						}}
																					>
																						Complete Status Breakdown:
																					</Typography>
																					{processedChartData.datasets.map(
																						(ds, dsIndex) => {
																							const dsValue =
																								ds.data[index] || 0;
																							if (dsValue === 0) return null;
																							const dsPercentage =
																								totalCount > 0
																									? (dsValue / totalCount) * 100
																									: 0;
																							const color = getVffColor(
																								ds.label,
																							);
																							const descriptiveLabel =
																								getVffStatusLabel(ds.label);

																							return (
																								<Box
																									key={dsIndex}
																									sx={{
																										display: "flex",
																										alignItems: "center",
																										marginBottom: "4px",
																										backgroundColor:
																											dsIndex === datasetIndex
																												? "rgba(204, 0, 0, 0.1)"
																												: "transparent",
																										padding: "4px 6px",
																										borderRadius: "0px",
																										border:
																											dsIndex === datasetIndex
																												? "1px solid #cc0000"
																												: "1px solid transparent",
																									}}
																								>
																									<Box
																										sx={{
																											width: "14px",
																											height: "14px",
																											backgroundColor: color.bg,
																											marginRight: "8px",
																											borderRadius: "0px",
																											flexShrink: 0,
																											border:
																												dsIndex === datasetIndex
																													? "1px solid #cc0000"
																													: "1px solid rgba(0,0,0,0.2)",
																										}}
																									/>
																									<Typography
																										sx={{
																											fontSize: "14px",
																											color: "#1e2832",
																											flex: 1,
																										}}
																									>
																										{descriptiveLabel}:{" "}
																										{dsValue.toLocaleString()}{" "}
																										parts (
																										{dsPercentage.toFixed(1)}%)
																									</Typography>
																								</Box>
																							);
																						},
																					)}
																				</Box>
																				{/* Summary section */}
																				<Box
																					sx={{
																						paddingTop: "8px",
																						marginTop: "8px",
																						backgroundColor: "#f0f0f0",
																						padding: "8px",
																						borderRadius: "0px",
																					}}
																				>
																					<Typography
																						sx={{
																							fontFamily: "MAN EUROPE_Bold",
																							fontSize: "14px",
																							color: "#1e2832",
																							marginBottom: "6px",
																						}}
																					>
																						<img
																							src="/checklist.png"
																							alt="summary"
																							style={{
																								width: "18px",
																								height: "18px",
																								objectFit: "contain",
																								marginRight: "6px",
																								verticalAlign: "middle",
																							}}
																						/>
																						Summary:
																					</Typography>
																					<Typography
																						sx={{
																							fontFamily: "MAN EUROPE_Bold",
																							fontSize: "14px",
																							color: "#1e2832",
																							marginBottom: "2px",
																						}}
																					>
																						• Total parts:{" "}
																						{totalCount.toLocaleString()}
																					</Typography>
																					<Typography
																						sx={{
																							fontFamily: "MAN EUROPE_Bold",
																							fontSize: "14px",
																							color: "#137c13",
																							marginBottom: "2px",
																						}}
																					>
																						• In Stock:{" "}
																						{availableCount.toLocaleString()} (
																						{totalCount > 0
																							? (
																									(availableCount /
																										totalCount) *
																									100
																								).toFixed(1)
																							: "0.0"}
																						%)
																					</Typography>
																					<Typography
																						sx={{
																							fontFamily: "MAN EUROPE_Bold",
																							fontSize: "14px",
																							color: "#cc0000",
																						}}
																					>
																						• Issues:{" "}
																						{(
																							totalCount - availableCount
																						).toLocaleString()}{" "}
																						(
																						{totalCount > 0
																							? (
																									((totalCount -
																										availableCount) /
																										totalCount) *
																									100
																								).toFixed(1)
																							: "0.0"}
																						%)
																					</Typography>
																				</Box>
																			</Box>
																		}
																		arrow
																		placement="top"
																		componentsProps={{
																			tooltip: {
																				sx: {
																					backgroundColor:
																						"rgba(255, 255, 255, 0.98)",
																					border: "2px solid #1e2832",
																					borderRadius: "0px",
																					boxShadow:
																						"0 8px 24px rgba(0, 0, 0, 0.2)",
																					maxWidth: "420px",
																					"& .MuiTooltip-arrow": {
																						color: "rgba(255, 255, 255, 0.98)",
																						"&:before": {
																							border: "2px solid #1e2832",
																						},
																					},
																				},
																			},
																		}}
																	>
																		<Box
																			onClick={() => {
																				// Handle click on stacked bar segment - redirect to VFF page
																				let vehicleNumber, date;
																				if (
																					viewMode === "timeline" ||
																					viewMode === "timeline-latest"
																				) {
																					vehicleNumber = item.vehicle_no;
																					date = item.date;
																				} else {
																					vehicleNumber = item.vehicle_no;
																					date = null;
																				}

																				const statusColor = dataset.label;
																				const baseUrl = `${window.location.origin}/vff`;
																				const urlParams = new URLSearchParams({
																					vehicle_filter: vehicleNumber,
																					status_color_filter: statusColor,
																					from_dashboard: "true",
																				});

																				// Include Part Number Prefix filter if explicitly applied by user (not 'all')
																				if (
																					selectedVffPartNo &&
																					selectedVffPartNo.length > 0 &&
																					!selectedVffPartNo.includes("all")
																				) {
																					urlParams.set(
																						"part_no_prefix",
																						selectedVffPartNo.join(","),
																					);
																				}

																				// Include Purchase Type filter if applied (not 'all')
																				// Don't apply default purchase types to show all parts regardless of type
																				if (
																					selectedVffPurchaseType &&
																					selectedVffPurchaseType.length > 0 &&
																					!selectedVffPurchaseType.includes(
																						"all",
																					)
																				) {
																					urlParams.set(
																						"purchase_type_filter",
																						selectedVffPurchaseType.join(","),
																					);
																				}

																				if (
																					(viewMode === "timeline" ||
																						viewMode === "timeline-latest") &&
																					date
																				) {
																					urlParams.set("date_filter", date);
																				}

																				const vffUrl = `${baseUrl}?${urlParams.toString()}`;
																				window.open(vffUrl, "_blank");
																			}}
																			sx={{
																				width: width,
																				height: "100%",
																				backgroundColor:
																					dataset.backgroundColor,
																				display: "flex",
																				alignItems: "center",
																				justifyContent: "center",
																				position: "relative",
																				cursor: "pointer",
																				zIndex: 1,

																				transition: "all 0.2s ease",
																				"&:hover": {
																					transform: "scaleY(1.05)",
																					zIndex: 10,
																					filter: "brightness(1.1)",
																					boxShadow:
																						"0 4px 12px rgba(0,0,0,0.3)",
																				},
																				"&:active": {
																					transform: "scaleY(0.95)",
																				},
																			}}
																		>
																			{" "}
																			{/* Always show value for segments */}
																			<Box
																				sx={{
																					display: "flex",
																					flexDirection: "column",
																					alignItems: "center",
																					justifyContent: "center",
																					width: "100%",
																					height: "100%",
																					textAlign: "center",
																				}}
																			>
																				{/* Always show the value, but adjust styling based on bar size */}
																				{value > 0 && (
																					<>
																						<Typography
																							sx={{
																								color: "black",
																								fontSize:
																									fixedScalePercentage > 8
																										? "14px"
																										: fixedScalePercentage > 5
																											? "12px"
																											: fixedScalePercentage > 2
																												? "10px"
																												: "9px",
																								fontFamily: "MAN EUROPE_Bold",
																								lineHeight: 1,
																								marginBottom:
																									fixedScalePercentage > 8
																										? "2px"
																										: "0px",
																							}}
																						>
																							{value.toLocaleString()}
																						</Typography>
																						{/* Only show percentage if there's enough space */}
																						{fixedScalePercentage > 8 && (
																							<Typography
																								sx={{
																									color: "black",
																									fontSize: "11px",
																									fontFamily: "MAN EUROPE_Bold",
																									lineHeight: 1,
																								}}
																							>
																								{percentage.toFixed(1)}%
																							</Typography>
																						)}
																					</>
																				)}
																			</Box>
																			{/* Subtle gradient overlay for depth */}
																			<Box
																				sx={{
																					position: "absolute",
																					top: 0,
																					left: 0,
																					right: 0,
																					bottom: 0,
																					background:
																						"linear-gradient(180deg, rgba(255,255,255,0.1) 0%, rgba(0,0,0,0.1) 100%)",
																					pointerEvents: "none",
																				}}
																			/>
																		</Box>
																	</Tooltip>
																);
															},
														)}
														{/* Empty state if no data */}
														{processedChartData.datasets.every(
															(ds) => (ds.data[index] || 0) === 0,
														) && (
															<Box
																sx={{
																	width: "100%",
																	height: "100%",
																	display: "flex",
																	alignItems: "center",
																	justifyContent: "center",
																	backgroundColor: "#f0f0f0",
																	borderRadius: "6px",
																}}
															>
																<Typography
																	sx={{
																		color: "#999",
																		fontSize: "12px",
																		fontFamily: "MAN EUROPE_Regular",
																		fontStyle: "italic",
																	}}
																>
																	All parts available
																</Typography>
															</Box>
														)}{" "}
													</Box>
													{/* Scale indicator */}
													<Box
														sx={{
															display: "flex",
															alignItems: "center",
															marginLeft: "10px",
															fontSize: "10px",
															color: "#666",
															fontFamily: "MAN EUROPE_Regular",
															whiteSpace: "nowrap",
														}}
													>
														<Typography
															sx={{
																fontSize: "9px",
																color: "#888",
																fontFamily: "MAN EUROPE_Regular",
															}}
														></Typography>
													</Box>
												</Box>
											</Box>
										);
									})}
								</Box>
							</Box>
						</Paper>
					</>
				) : (
					<Typography sx={{ padding: "0 0 0 10px" }}>
						Content loading, Please wait...
					</Typography>
				)}

				{/* Shared band editor popover — radio options + Save/Cancel, per vehicle */}
				<Popover
					open={Boolean(bandMenuAnchor)}
					anchorEl={bandMenuAnchor}
					onClose={closeBandMenu}
					anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
					transformOrigin={{ vertical: "top", horizontal: "left" }}
					slotProps={{
						paper: {
							sx: {
								p: 2,
								minWidth: 200,
								borderRadius: "8px",
								boxShadow: "0 4px 16px rgba(0,0,0,0.2)",
							},
						},
					}}
				>
					<Typography
						sx={{
							fontFamily: "MAN EUROPE_Bold",
							fontSize: "13px",
							color: "#1e2832",
							mb: 1,
						}}
					>
						Band {bandMenuVehicle ? `— ${bandMenuVehicle}` : ""}
					</Typography>
					<FormGroup>
						{BAND_OPTIONS.map((option) => (
							<FormControlLabel
								key={option}
								control={
									<Checkbox
										size="small"
										checked={bandDraft.includes(option)}
										onChange={() => toggleBandOption(option)}
										sx={{
											color: "#cc0000",
											"&.Mui-checked": { color: "#cc0000" },
										}}
									/>
								}
								label={<span style={{ fontSize: "13px" }}>{option}</span>}
								disabled={Boolean(savingVehicle)}
							/>
						))}
					</FormGroup>

					{bandError && (
						<Typography
							sx={{
								fontFamily: "MAN EUROPE_Regular",
								fontSize: "12px",
								color: "#cc0000",
								mt: 1,
							}}
						>
							{bandError}
						</Typography>
					)}

					<Divider sx={{ my: 1.5 }} />

					<Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
						<Button
							size="small"
							variant="outlined"
							onClick={closeBandMenu}
							disabled={Boolean(savingVehicle)}
							sx={{
								fontFamily: "MAN EUROPE_Bold",
								fontSize: "0.7rem",
								borderColor: "#666",
								color: "#666",
								minWidth: "70px",
								height: "30px",
								"&:hover": {
									borderColor: "#444",
									backgroundColor: "rgba(102,102,102,0.04)",
								},
							}}
						>
							Cancel
						</Button>
						<Button
							size="small"
							variant="contained"
							onClick={saveBandMenu}
							disabled={Boolean(savingVehicle)}
							startIcon={
								savingVehicle ? (
									<CircularProgress size={14} sx={{ color: "#fff" }} />
								) : null
							}
							sx={{
								fontFamily: "MAN EUROPE_Bold",
								fontSize: "0.7rem",
								backgroundColor: "#cc0000",
								color: "#fff",
								minWidth: "80px",
								height: "30px",
								"&:hover": { backgroundColor: "#aa0000" },
								"&:disabled": { backgroundColor: "#7a3b3b", color: "#ddd" },
							}}
						>
							{savingVehicle ? "Saving..." : "Save"}
						</Button>
					</Box>
				</Popover>
			</Box>
		);
	},
);

export default VffDashboard;
